// Excel template generators, styled after the reference packing-list workbook:
// navy title banners, blue table headers with light-blue banding, green
// auto-calculated cells and blue "type here" cells.
//
// Built with ExcelJS (not SheetJS) because dropdowns (data validation) can
// only be written by ExcelJS.
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Package types offered in the form's dropdown, the Excel template's
// dropdown, and recognized by the packing list importer
export const PACKAGE_TYPE_OPTIONS = ['Box', 'Wooden Box', 'Wooden Crate', 'Cardboard Box', 'Pallet', 'Container'];

const CONDITION_OPTIONS = ['New', 'Used', 'Reconditioned'];

// Palette lifted from the reference workbook
const NAVY = 'FF1F3864';         // title banners, outer frame
const HEADER_BLUE = 'FF2F5496';  // table header rows
const BORDER_BLUE = 'FF8EAADB';  // thin inner borders
const BAND_FILL = 'FFD9E2F3';    // alternating row banding
const TOTAL_FILL = 'FFB4C7E7';   // TOTAL rows
const LABEL_FILL = 'FFEDF1F8';   // metadata label cells / address blocks
const INPUT_BLUE = 'FF0000FF';   // cells the user is expected to edit
const FORMULA_GREEN = 'FF008000';// auto-calculated cells
const WHITE = 'FFFFFFFF';

const WEIGHT_FMT = '#,##0.00" Kg"';
const DECIMAL_FMT = '#,##0.00';

const thin = { style: 'thin', color: { argb: BORDER_BLUE } };
const medium = { style: 'medium', color: { argb: NAVY } };

const solidFill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

// Thin borders inside the range, medium navy frame around it
const frameRange = (sheet, top, left, bottom, right) => {
  for (let r = top; r <= bottom; r++) {
    for (let c = left; c <= right; c++) {
      sheet.getCell(r, c).border = {
        top: r === top ? medium : thin,
        bottom: r === bottom ? medium : thin,
        left: c === left ? medium : thin,
        right: c === right ? medium : thin
      };
    }
  }
};

const addTitleBanner = (sheet, range, text) => {
  sheet.mergeCells(range);
  const cell = sheet.getCell(range.split(':')[0]);
  cell.value = text;
  cell.font = { bold: true, size: 14, color: { argb: WHITE } };
  cell.fill = solidFill(NAVY);
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(cell.row).height = 24;
};

const styleHeaderCell = (cell) => {
  cell.font = { bold: true, color: { argb: WHITE } };
  cell.fill = solidFill(HEADER_BLUE);
  cell.alignment = { horizontal: 'center' };
};

const conditionValidation = {
  type: 'list',
  allowBlank: true,
  formulae: [`"${CONDITION_OPTIONS.join(',')}"`],
  showErrorMessage: true,
  errorTitle: 'Invalid condition',
  error: `Choose one of: ${CONDITION_OPTIONS.join(', ')}`
};

const packageTypeValidation = {
  type: 'list',
  allowBlank: true,
  formulae: [`"${PACKAGE_TYPE_OPTIONS.join(',')}"`],
  showErrorMessage: true,
  errorTitle: 'Invalid box type',
  error: `Choose one of: ${PACKAGE_TYPE_OPTIONS.join(', ')}`
};

// ---- Packing list template ----
// One BOX CONFIGURATION summary sheet (auto-filled from the box sheets via
// formulas) plus one sheet per box: box name, type, dimensions and gross
// weight above an Item No / Spare Part No / Description / Qty / Weight /
// Condition table with a TOTAL row.

const buildBoxSheet = (workbook, boxName, meta, items) => {
  const sheet = workbook.addWorksheet(boxName);
  [22.5, 21.7, 33.3, 9.2, 15, 18.3].forEach((width, i) => { sheet.getColumn(i + 1).width = width; });

  addTitleBanner(sheet, 'A1:F1', boxName);

  const metaRows = [
    ['Box Type:', meta.type],
    ['Dimensions (W x L x H):', meta.dimensions],
    ['Gross Weight:', meta.weight],
    ['Contents:', meta.contents]
  ];
  metaRows.forEach(([label, value], i) => {
    const labelCell = sheet.getCell(2 + i, 1);
    labelCell.value = label;
    labelCell.font = { bold: true };
    labelCell.fill = solidFill(LABEL_FILL);
    sheet.getCell(2 + i, 2).value = value;
  });
  sheet.getCell('B2').font = { color: { argb: INPUT_BLUE } };
  sheet.getCell('B2').dataValidation = packageTypeValidation;
  sheet.getCell('B4').font = { color: { argb: INPUT_BLUE } };
  sheet.getCell('B4').numFmt = WEIGHT_FMT;
  frameRange(sheet, 2, 1, 5, 2);

  const headerRow = 7;
  ['Item No', 'Spare Part No', 'Description', 'Qty', 'Weight (KG)', 'Condition'].forEach((header, i) => {
    const cell = sheet.getCell(headerRow, i + 1);
    cell.value = header;
    styleHeaderCell(cell);
  });

  const firstItemRow = headerRow + 1;
  items.forEach((item, idx) => {
    const r = firstItemRow + idx;
    const serial = sheet.getCell(r, 1);
    serial.value = idx + 1;
    serial.alignment = { horizontal: 'center' };

    const partNo = sheet.getCell(r, 2);
    partNo.value = item.partNo;
    partNo.numFmt = '@';

    sheet.getCell(r, 3).value = item.description;

    const qty = sheet.getCell(r, 4);
    qty.value = item.qty;
    qty.alignment = { horizontal: 'center' };

    const weight = sheet.getCell(r, 5);
    weight.value = item.weight;
    weight.numFmt = DECIMAL_FMT;

    const condition = sheet.getCell(r, 6);
    condition.value = item.condition;
    condition.font = { color: { argb: INPUT_BLUE } };
    condition.alignment = { horizontal: 'center' };
    condition.dataValidation = conditionValidation;

    if (idx % 2 === 1) {
      for (let c = 1; c <= 6; c++) {
        sheet.getCell(r, c).fill = solidFill(BAND_FILL);
      }
    }
  });

  const totalRow = firstItemRow + items.length;
  sheet.mergeCells(totalRow, 1, totalRow, 3);
  const totalLabel = sheet.getCell(totalRow, 1);
  totalLabel.value = 'TOTAL';
  totalLabel.font = { bold: true };
  totalLabel.alignment = { horizontal: 'right' };

  const qtyTotal = sheet.getCell(totalRow, 4);
  qtyTotal.value = {
    formula: `SUM(D${firstItemRow}:D${totalRow - 1})`,
    result: items.reduce((sum, item) => sum + item.qty, 0)
  };
  qtyTotal.font = { bold: true };
  qtyTotal.alignment = { horizontal: 'center' };

  const weightTotal = sheet.getCell(totalRow, 5);
  weightTotal.value = {
    formula: `SUM(E${firstItemRow}:E${totalRow - 1})`,
    result: items.reduce((sum, item) => sum + item.weight, 0)
  };
  weightTotal.font = { bold: true };
  weightTotal.numFmt = DECIMAL_FMT;

  for (let c = 1; c <= 6; c++) {
    sheet.getCell(totalRow, c).fill = solidFill(TOTAL_FILL);
  }

  frameRange(sheet, headerRow, 1, totalRow, 6);
};

const addAddressBlock = (sheet, startRow, title, placeholder) => {
  sheet.mergeCells(startRow, 1, startRow, 4);
  const titleCell = sheet.getCell(startRow, 1);
  titleCell.value = title;
  styleHeaderCell(titleCell);

  sheet.mergeCells(startRow + 1, 1, startRow + 2, 4);
  const bodyCell = sheet.getCell(startRow + 1, 1);
  bodyCell.value = placeholder;
  bodyCell.font = { color: { argb: INPUT_BLUE } };
  bodyCell.fill = solidFill(LABEL_FILL);
  bodyCell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };

  frameRange(sheet, startRow, 1, startRow + 2, 4);
};

export const buildPackingTemplateWorkbook = () => {
  const workbook = new ExcelJS.Workbook();

  const boxes = [
    {
      name: 'BOX NO 1',
      type: 'Wooden Box',
      dimensions: '900mm x 1100mm x 970mm',
      weight: 528,
      contents: 'ENGINE SPARE PARTS',
      items: [
        { partNo: '3801631', description: 'Cylinder head assembly', qty: 2, weight: 45.5, condition: 'New' },
        { partNo: '4089478', description: 'Gasket set', qty: 1, weight: 3.2, condition: 'New' },
        { partNo: '111 0006/0008', description: 'Big end Bearing shell-Lower', qty: 2, weight: 0, condition: 'New' }
      ]
    },
    {
      name: 'BOX NO 2',
      type: 'Pallet',
      dimensions: '1010mm x 800mm x 860mm',
      weight: 215,
      contents: 'ENGINE TOOL BOX',
      items: [
        { partNo: '3538623', description: 'Turbocharger', qty: 1, weight: 28, condition: 'Reconditioned' }
      ]
    }
  ];

  // Summary sheet: each row mirrors its box sheet via formulas, so users only
  // need to edit the box sheets
  const configSheet = workbook.addWorksheet('BOX CONFIGURATION');
  [21.7, 18.3, 31.7, 15].forEach((width, i) => { configSheet.getColumn(i + 1).width = width; });
  addTitleBanner(configSheet, 'A1:D1', 'BOX CONFIGURATION');

  const configHeaderRow = 3;
  ['Box', 'Type', 'Dimensions (W x L x H)', 'Weight'].forEach((header, i) => {
    const cell = configSheet.getCell(configHeaderRow, i + 1);
    cell.value = header;
    styleHeaderCell(cell);
  });

  boxes.forEach((box, idx) => {
    const r = configHeaderRow + 1 + idx;
    configSheet.getCell(r, 1).value = box.name;

    const linked = [
      [2, 'B2', box.type],
      [3, 'B3', box.dimensions],
      [4, 'B4', box.weight]
    ];
    linked.forEach(([col, ref, result]) => {
      const cell = configSheet.getCell(r, col);
      cell.value = { formula: `'${box.name}'!${ref}`, result };
      cell.font = { color: { argb: FORMULA_GREEN } };
      cell.alignment = { horizontal: 'center' };
    });
    configSheet.getCell(r, 4).numFmt = WEIGHT_FMT;

    if (idx % 2 === 1) {
      for (let c = 1; c <= 4; c++) {
        configSheet.getCell(r, c).fill = solidFill(BAND_FILL);
      }
    }
  });

  const configTotalRow = configHeaderRow + 1 + boxes.length;
  configSheet.mergeCells(configTotalRow, 1, configTotalRow, 3);
  const configTotalLabel = configSheet.getCell(configTotalRow, 1);
  configTotalLabel.value = 'TOTAL WEIGHT (ALL BOXES)';
  configTotalLabel.font = { bold: true };
  configTotalLabel.alignment = { horizontal: 'right' };
  const configTotalWeight = configSheet.getCell(configTotalRow, 4);
  configTotalWeight.value = {
    formula: `SUM(D${configHeaderRow + 1}:D${configTotalRow - 1})`,
    result: boxes.reduce((sum, box) => sum + box.weight, 0)
  };
  configTotalWeight.font = { bold: true };
  configTotalWeight.numFmt = WEIGHT_FMT;
  configTotalWeight.alignment = { horizontal: 'center' };
  for (let c = 1; c <= 4; c++) {
    configSheet.getCell(configTotalRow, c).fill = solidFill(TOTAL_FILL);
  }
  frameRange(configSheet, configHeaderRow, 1, configTotalRow, 4);

  addAddressBlock(configSheet, configTotalRow + 2, 'PICKUP ADDRESS', 'Sender company, street, city, country');
  addAddressBlock(configSheet, configTotalRow + 6, 'DELIVERY ADDRESS', 'Receiver company, street, city, country');

  boxes.forEach(box => buildBoxSheet(workbook, box.name, box, box.items));

  return workbook;
};

export const downloadPackingTemplate = async () => {
  const workbook = buildPackingTemplateWorkbook();
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, 'packing-list-template.xlsx');
};

// ---- Import template for offers / invoices / pro forma invoices / POs ----
// A single small sheet matching the columns the "Import File" button parses.
// Careful: the importer treats the first row containing ITEM / DESCRIPTION /
// QTY as the header row, so the title and note must not contain those words.

const IMPORT_SAMPLE_ITEMS = [
  { partNo: '3801631', description: 'Cylinder head assembly', qty: 2, unitPrice: 1450, condition: 'New', remarks: '', availability: 'Ex stock - subject unsold' },
  { partNo: '4089478', description: 'Gasket set', qty: 1, unitPrice: 320, condition: 'New', remarks: '', availability: 'Ex stock - subject unsold' },
  { partNo: '3538623', description: 'Turbocharger', qty: 1, unitPrice: 5200, condition: 'Reconditioned', remarks: 'Overhauled 2024', availability: '2-3 weeks' }
];

const IMPORT_SPARE_ROWS = 7;

export const buildImportTemplateWorkbook = () => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('ITEMS');
  [8, 40, 18, 8, 14, 14, 16, 24, 24].forEach((width, i) => { sheet.getColumn(i + 1).width = width; });

  addTitleBanner(sheet, 'A1:I1', 'OFFER / INVOICE / PURCHASE ORDER \u2014 IMPORT TEMPLATE');

  sheet.mergeCells('A2:I2');
  const note = sheet.getCell('A2');
  note.value = 'Fill in the rows below, then upload this file with the Import File button. Keep the header row as it is.';
  note.font = { italic: true, color: { argb: 'FF666666' } };
  note.alignment = { horizontal: 'left' };

  const headerRow = 4;
  ['Item No', 'Description', 'Part No', 'Qty', 'Unit Price', 'Total Price', 'Condition', 'Remarks', 'Availability'].forEach((header, i) => {
    const cell = sheet.getCell(headerRow, i + 1);
    cell.value = header;
    styleHeaderCell(cell);
  });

  const firstItemRow = headerRow + 1;
  const lastItemRow = headerRow + IMPORT_SAMPLE_ITEMS.length + IMPORT_SPARE_ROWS;

  IMPORT_SAMPLE_ITEMS.forEach((item, idx) => {
    const r = firstItemRow + idx;
    const serial = sheet.getCell(r, 1);
    serial.value = idx + 1;
    serial.alignment = { horizontal: 'center' };

    sheet.getCell(r, 2).value = item.description;

    const partNo = sheet.getCell(r, 3);
    partNo.value = item.partNo;
    partNo.numFmt = '@';

    const qty = sheet.getCell(r, 4);
    qty.value = item.qty;
    qty.alignment = { horizontal: 'center' };

    const unitPrice = sheet.getCell(r, 5);
    unitPrice.value = item.unitPrice;
    unitPrice.numFmt = DECIMAL_FMT;

    const totalPrice = sheet.getCell(r, 6);
    totalPrice.value = { formula: `D${r}*E${r}`, result: item.qty * item.unitPrice };
    totalPrice.numFmt = DECIMAL_FMT;
    totalPrice.font = { color: { argb: FORMULA_GREEN } };

    const condition = sheet.getCell(r, 7);
    condition.value = item.condition;
    condition.alignment = { horizontal: 'center' };

    sheet.getCell(r, 8).value = item.remarks;
    sheet.getCell(r, 9).value = item.availability;
  });

  // Empty rows ready to be filled in (condition dropdown already in place)
  for (let r = firstItemRow; r <= lastItemRow; r++) {
    sheet.getCell(r, 7).dataValidation = conditionValidation;
    if ((r - firstItemRow) % 2 === 1) {
      for (let c = 1; c <= 9; c++) {
        sheet.getCell(r, c).fill = solidFill(BAND_FILL);
      }
    }
  }

  const totalRow = lastItemRow + 1;
  sheet.mergeCells(totalRow, 1, totalRow, 3);
  const totalLabel = sheet.getCell(totalRow, 1);
  totalLabel.value = 'TOTAL';
  totalLabel.font = { bold: true };
  totalLabel.alignment = { horizontal: 'right' };

  const qtyTotal = sheet.getCell(totalRow, 4);
  qtyTotal.value = {
    formula: `SUM(D${firstItemRow}:D${lastItemRow})`,
    result: IMPORT_SAMPLE_ITEMS.reduce((sum, item) => sum + item.qty, 0)
  };
  qtyTotal.font = { bold: true };
  qtyTotal.alignment = { horizontal: 'center' };

  const priceTotal = sheet.getCell(totalRow, 6);
  priceTotal.value = {
    formula: `SUM(F${firstItemRow}:F${lastItemRow})`,
    result: IMPORT_SAMPLE_ITEMS.reduce((sum, item) => sum + item.qty * item.unitPrice, 0)
  };
  priceTotal.font = { bold: true };
  priceTotal.numFmt = DECIMAL_FMT;

  for (let c = 1; c <= 9; c++) {
    sheet.getCell(totalRow, c).fill = solidFill(TOTAL_FILL);
  }
  frameRange(sheet, headerRow, 1, totalRow, 9);

  return workbook;
};

export const downloadImportTemplate = async () => {
  const workbook = buildImportTemplateWorkbook();
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, 'import-template.xlsx');
};
